<template>
  <div>
    <div>
      <button ref="button" class="colors-button">
        <span
          class="selected-color"
          :class="{ [`selected-color-${value}`]: true }"
        />
        <ChevronDownIcon />
      </button>
    </div>
    <div ref="content" class="colors-button-content">
      <div
        v-for="color in colors"
        :key="color"
        class="color-wrapper"
        :class="color === value && 'selected'"
        @click="onSelectColor(color)"
      >
        <span
          class="color-palette"
          :class="{ [`background-color-${color}`]: true }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, ref, useAttrs } from "vue";
import { useTippy } from "vue-tippy";

import { CalendarColor } from "@ctrl/controller-rpc";

import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";

const props = withDefaults(
  defineProps<{
    value?: CalendarColor;
    /** Tippy placement for the palette popover. */
    placement?:
      | "top"
      | "top-start"
      | "top-end"
      | "right"
      | "right-start"
      | "right-end"
      | "bottom"
      | "bottom-start"
      | "bottom-end"
      | "left"
      | "left-start"
      | "left-end"
      | "auto"
      | "auto-start"
      | "auto-end";
  }>(),
  {
    value: "a",
    placement: "left",
  }
);

const emit = defineEmits<{
  (e: "select", key: CalendarColor): void;
}>();

const colors: Array<CalendarColor> = [
  "a",
  "b",
  "c",
  "d",
  "e",
  "f",
  "g",
  "h",
  "i",
];

const button = ref<HTMLDivElement>();
const content = ref<HTMLDivElement>();

const { hide, destroy } = useTippy(button, {
  animateFill: false,
  appendTo: "parent",
  placement: props.placement,
  content: () => content.value,
  duration: 100,
  interactive: true,
  trigger: "click",
  arrow: true,
  theme: "menu",
  ...useAttrs(),
});
onBeforeUnmount(() => destroy());
const onSelectColor = (key: CalendarColor) => {
  hide();
  emit("select", key);
};
</script>

<style scoped lang="scss">
.colors-button {
  height: 24px;
  padding: 7px;
  border-radius: $border-radius-m;
  display: flex;
  @include themed() {
    border: 1px solid t($grey-10);
  }

  .selected-color {
    width: 10px;
    height: 10px;
    opacity: 0.8;
    border-radius: $border-radius-s;
    margin-inline-end: 6px;

    &.selected-color-a {
      background-color: $calendar-a-50;
    }
    &.selected-color-b {
      background-color: $calendar-b-50;
    }
    &.selected-color-c {
      background-color: $calendar-c-50;
    }
    &.selected-color-d {
      background-color: $calendar-d-50;
    }
    &.selected-color-e {
      background-color: $calendar-e-50;
    }
    &.selected-color-f {
      background-color: $calendar-f-50;
    }
    &.selected-color-g {
      background-color: $calendar-g-50;
    }
    &.selected-color-h {
      background-color: $calendar-h-50;
    }
    &.selected-color-i {
      background-color: $calendar-i-50;
    }
  }
}

.colors-button-content {
  padding: 10px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-row-gap: 4px;
  grid-column-gap: 4px;
}

.color-wrapper {
  padding: 6px;
  border-radius: $border-radius-m;
  @include transition(background-color 100ms ease-out);

  &.selected {
    @include themed() {
      border: 1px solid t($grey-15);
    }
  }

  &:hover {
    @include themed() {
      background-color: t($grey-15);
    }
  }

  .color-palette {
    border-radius: $border-radius-s;
    height: 16px;
    width: 16px;
    display: block;

    &.background-color-a {
      background-color: $calendar-a-50;
    }
    &.background-color-b {
      background-color: $calendar-b-50;
    }
    &.background-color-c {
      background-color: $calendar-c-50;
    }
    &.background-color-d {
      background-color: $calendar-d-50;
    }
    &.background-color-e {
      background-color: $calendar-e-50;
    }
    &.background-color-f {
      background-color: $calendar-f-50;
    }
    &.background-color-g {
      background-color: $calendar-g-50;
    }
    &.background-color-h {
      background-color: $calendar-h-50;
    }
    &.background-color-i {
      background-color: $calendar-i-50;
    }
  }
}
</style>
